import React, { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { isEmpty, isNil } from 'lodash'

import { MultiSelect } from '@/components/forms/MultiSelect'
import { FormField } from '@/components/forms/FormField'

// Built-in Five9 fields always available as campaign-name sources. Mirrors the
// legacy single-select options; extend here to expose more built-ins.
const BUILTIN_OPTIONS = [
  { value: 'Call.campaign_name', label: 'Call Campaign Name' },
  { value: 'Call.skill_name', label: 'Call Skill Name' },
]

// Map a legacy single `voip_campaign_name` string to the new list of keys so an
// existing config's setting is reflected in the multi-select on load.
export const seedFromLegacy = (legacy) => {
  if (isEmpty(legacy)) {
    return []
  }
  if (legacy === 'campaignName_skillName') {
    return ['Call.campaign_name', 'Call.skill_name']
  }
  return [legacy]
}

/**
 * Five9-only campaign-name picker. Replaces the single `voip_campaign_name`
 * dropdown with a multi-select bound to `voip_campaign_name_fields`: pick one
 * (single value) or several (concatenated with "_" in selection order by the
 * backend). Options are the built-ins plus the org's configured custom fields,
 * read reactively from Formik context.
 *
 * Self-contained (injected via the schema `raw`/`as` hatch): stores an array of
 * string keys directly (the backend shape), so nothing shared serializes it.
 */
export const Five9CampaignNameSelect = () => {
  const { values, setFieldValue } = useFormikContext()

  const customFields = Array.isArray(values.custom_fields) ? values.custom_fields : []
  const options = [
    ...BUILTIN_OPTIONS,
    ...customFields
      .filter((field) => !isEmpty(field?.key))
      .map((field) => ({ value: field.key, label: field.key })),
  ]
  const validValues = options.map((option) => option.value)

  const legacy = values.voip_campaign_name
  const fields = Array.isArray(values.voip_campaign_name_fields)
    ? values.voip_campaign_name_fields
    : []
  // The edit form mounts before the config loads; `type` is set once it has.
  const configLoaded = !isEmpty(values.type)

  // Seed from the legacy single-string field once the config loads, for
  // backward compatibility. Runs when the value arrives (not just on mount) and
  // only seeds once so the selection can still be cleared afterward.
  const [seeded, setSeeded] = useState(false)
  useEffect(() => {
    if (seeded || !configLoaded) {
      return
    }
    if (!isEmpty(fields)) {
      setSeeded(true)
      return
    }
    const seed = seedFromLegacy(legacy)
    if (!isEmpty(seed)) {
      setFieldValue('voip_campaign_name_fields', seed)
    }
    setSeeded(true)
  }, [configLoaded, legacy, values.voip_campaign_name_fields, seeded, setFieldValue])

  const selectedKeys = fields.filter((key) => validValues.includes(key))
  const labelFor = (key) => options.find((option) => option.value === key)?.label || key
  const selectedOptions = selectedKeys.map((key) => ({ value: key, label: labelFor(key) }))

  const handleChange = (selected) => {
    const chosen = isNil(selected) ? [] : selected
    setFieldValue(
      'voip_campaign_name_fields',
      chosen.map((option) => option.value)
    )
    // Never write the legacy `voip_campaign_name`: the backend still reads it as a
    // fallback, so it stays the permanent legacy source of truth. It is only ever
    // read here to seed the multi-select on load (see the effect above).
  }

  const preview = selectedKeys.map(labelFor).join('_')

  return (
    <FormField data-testid="five9-campaign-name">
      <label htmlFor="voip_campaign_name_fields">VOIP Campaign Name</label>
      <div className="form-caption">
        Select one or more fields. Multiple fields are concatenated with &ldquo;_&rdquo; in the
        order selected.
      </div>
      <MultiSelect
        // Selection order drives the concatenated name, so show every chip
        // wrapping onto new lines instead of condensing to a count.
        condenseOptions={false}
        showFullOptions
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        inputId="voip_campaign_name_fields"
      />
      {!isEmpty(selectedKeys) && (
        <div className="form-caption" data-testid="five9-campaign-preview">
          Preview: {preview}
        </div>
      )}
    </FormField>
  )
}
